import { type BaseQueryFn, createApi } from "@reduxjs/toolkit/query/react";
import {
  Realtime,
  type ChannelParams,
  type ChannelState,
  type InboundMessage,
} from "ably";
import { getLogger } from "@momentum/logger";
import * as vcdiffPlugin from "@ably/vcdiff-decoder";
import { createAction, type WithSlice } from "@reduxjs/toolkit";
import {
  dynamicMiddleware,
  rootReducer,
  type RootState,
} from "@momentum/redux-store";

const logger = getLogger().child().withMetadata({
  source: "realtimeApi",
});

const realtime = new Realtime({
  key: import.meta.env.VITE_ABLY_SUBSCRIBE_KEY,
  plugins: {
    vcdiff: vcdiffPlugin,
  },
});

const baseQuery: BaseQueryFn<unknown, RealtimeMessage[], unknown> = async (
  args,
  api,
  extraOptions
) => {
  return {
    data: [],
  };
};

export const channelChangeState = createAction<{
  channel: string;
  params?: ChannelParams;
  previous: ChannelState;
  current: ChannelState;
}>("realtime/channelChangeState");

export interface RealtimeMessage {
  id: string;
  name?: string;
  timestamp: number;
  data: unknown;
}

export const messageReceived = createAction<{
  channel: string;
  params?: ChannelParams;
  message: RealtimeMessage;
}>("realtime/messageReceived");

export const realtimeApi = createApi({
  reducerPath: "realtimeApi",
  baseQuery,
  endpoints: (builder) => ({
    subscribeChannel: builder.query<
      RealtimeMessage[],
      { channel: string; params?: ChannelParams }
    >({
      query: (arg) => arg,
      async onCacheEntryAdded(arg, api) {
        await api.cacheDataLoaded;

        const channel = realtime.channels.get(arg.channel, {
          params: arg.params ?? {},
        });

        const listener = async (message: InboundMessage) => {
          const realtimeMessage = {
            id: message.id,
            name: message.name,
            timestamp: message.timestamp,
            data: JSON.parse(message.data),
          };
          api.updateCachedData((draft) => {
            draft.push(realtimeMessage);
          });

          api.dispatch(
            messageReceived({
              channel: arg.channel,
              params: arg.params,
              message: realtimeMessage,
            })
          );
        };

        channel.on((changeState) => {
          api.dispatch(
            channelChangeState({
              channel: arg.channel,
              params: arg.params,
              previous: changeState.previous,
              current: changeState.current,
            })
          );
        });

        await channel.subscribe(listener);

        await api.cacheEntryRemoved;

        channel.unsubscribe(listener);
        await channel.detach();
        realtime.channels.release(arg.channel);
      },
    }),
  }),
});

rootReducer.inject(realtimeApi);

declare module "@momentum/redux-store" {
  export interface LazyLoadedSlices extends WithSlice<typeof realtimeApi> {}
}

dynamicMiddleware.addMiddleware(realtimeApi.middleware);

export const selectRealtimeApiSlice = (state: RootState) => state.realtimeApi;
